
import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from "@/components/ui/button";
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { Helmet } from 'react-helmet-async';

const GladsaxeLocation = () => {
  const features = [
    {
      title: "Centralt i Gladsaxe",
      description: "Undervisning tæt på Gladsaxevej. Undervisning nær Meny på Søborg Hovedgade.",
      icon: "location"
    },
    {
      title: "Fleksible tider",
      description: "Morgen-, eftermiddags- og aftentimer på hverdage samt weekend-undervisning efter aftale.",
      icon: "clock"
    },
    {
      title: "Alle niveauer",
      description: "Fra nybegyndere til øvede - undervisning tilpasset dit niveau, uanset om du er barn eller voksen.",
      icon: "music"
    },
    {
      title: "Erfaren underviser",
      description: "Undervisning med Oscar Gilbert, konservatorieuddannet pianist med flere års undervisningserfaring.",
      icon: "award"
    }
  ];

  const testimonials = [
    {
      name: "Peter Hansen",
      location: "Gladsaxe",
      text: "Min søn trives fantastisk med klaverundervisningen hos Oscar. Det er tydeligt at mærke, at Oscar forstår at motivere og engagere børn på en positiv måde.",
      stars: 5
    },
    {
      name: "Anne Jensen",
      location: "Gladsaxe",
      text: "Jeg har haft stor glæde af Oscars undervisning. Hans tilgang er både struktureret og inspirerende, og jeg har udviklet mig langt mere, end jeg havde troet muligt.",
      stars: 5
    },
    {
      name: "Søren Nielsen",
      location: "Gladsaxe",
      text: "Som voksen nybegynder var jeg nervøs for at starte, men Oscar skaber et trygt læringsmiljø, hvor det er okay at lave fejl og stille spørgsmål.",
      stars: 5
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Klaverundervisning i Gladsaxe | Få en gratis prøvetime!</title>
        <meta name="description" content="Professionel klaverundervisning i Gladsaxe med Oscar Gilbert. Book en gratis prøvetime og start din musikalske rejse i dag!" />
      </Helmet>
      
      <LocationHero 
        title="Klaverundervisning i Gladsaxe"
        description="Professionel klaverundervisning med Oscar Gilbert i Gladsaxe. Undervisning i trygge rammer for både børn og voksne på alle niveauer."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Klaverundervisning i <span className="text-piano-gold">Gladsaxe</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                I Gladsaxe tilbyder Oscar Gilbert professionel klaverundervisning med fokus på personlig udvikling og musikalsk glæde. Undervisningen foregår i lyse og inspirerende lokaler med adgang til kvalitetsinstrumenter.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Uanset om du drømmer om at spille klassisk musik, pop, jazz eller filmmusik, kan du få skræddersyet undervisning der passer til netop dine interesser og ambitioner.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Som pianist med konservatorieuddannelse og mange års undervisningserfaring sikrer Oscar, at du får teknisk solid og musikalsk inspirerende undervisning i et positivt og støttende miljø.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Book Gratis Prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/undervisning">Se Undervisningstilbud</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
              <div className="relative bg-white p-3 rounded-lg shadow-xl">
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" 
                    alt="Klaverundervisning i Gladsaxe" 
                    className="w-full h-full object-cover object-center object-[50%_35%]"
                    width="400"
                    height="400"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Gladsaxe" />
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Find vej til undervisningen</h2>
              <p className="text-lg text-gray-600">
                Klaverundervisningen foregår på Niels Finsens Alle 86, 2860 Søborg, let tilgængeligt fra Gladsaxe.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl mb-10">
              <div className="overflow-hidden rounded-md">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12607.679143124358!2d12.514096630724325!3d55.734728901797794!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743668909093!5m2!1sda!2sdk" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokation på kort">
                </iframe>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Adresse</h3>
                  <p className="text-gray-700">
                    Niels Finsens Alle 86<br />
                    2860 Søborg
                  </p>
                  <div className="space-y-2">
                    <p className="text-gray-700"><strong>Transport fra Gladsaxe:</strong></p>
                    <p className="text-gray-700">• Kort transporttid fra Gladsaxe</p>
                    <p className="text-gray-700">• Gode busforbindelser</p>
                    <p className="text-gray-700">• Gratis parkering ved undervisningslokalet</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Kontakt</h3>
                  <p className="text-gray-700">
                    Telefon: <a href="tel:+4540817127" className="text-piano-gold hover:underline">+45 40 81 71 27</a>
                  </p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:kontakt@pianoundervisning.dk" className="text-piano-gold hover:underline">kontakt@pianoundervisning.dk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationCTA locationName="Gladsaxe" />
    </Layout>
  );
};

export default GladsaxeLocation;
